import type { PanelRow, Section } from "./panels.ts";

/**
 * Valideringsregler för centralspec-rader. Returnerar varningar som chips
 * under cellen i tabellen — inga hårda blockeringar. Referenser:
 *   - SS 436 40 00 / SS 437 01 00: kabelarea vs säkringsström
 *   - IEC 60364 / SS-EN 61140: JFB-krav för uttag upp till 20 A
 *   - SS-EN 61439: centralkonstruktion
 */

export type PanelWarning = {
  rowId: string;
  /** Cell-id (kolumnen) där varningen ska visas. */
  column:
    | "designation"
    | "component"
    | "ampRating"
    | "cable"
    | "usage"
    | "sectionId";
  severity: "warning" | "info";
  code: string;          // t.ex. "CABLE_AREA_TOO_SMALL"
  message: string;       // svensk text
  reference?: string;    // t.ex. "SS 436 40 00"
};

/** Max säkringsström för en given kabelarea (PVC-isolerad Cu i byggnad). */
const MAX_AMP_FOR_AREA: ReadonlyArray<{ area: number; maxA: number }> = [
  { area: 1.0, maxA: 10 },
  { area: 1.5, maxA: 16 },
  { area: 2.5, maxA: 20 },
  { area: 4, maxA: 25 },
  { area: 6, maxA: 32 },
  { area: 10, maxA: 50 },
  { area: 16, maxA: 63 },
  { area: 25, maxA: 80 },
  { area: 35, maxA: 100 },
  { area: 50, maxA: 125 },
  { area: 70, maxA: 160 },
  { area: 95, maxA: 200 },
  { area: 120, maxA: 250 },
];

export function maxAmpForArea(areaMm2: number): number | undefined {
  const row = MAX_AMP_FOR_AREA.find((r) => r.area === areaMm2);
  return row?.maxA;
}

/** Användningskoder som enligt SS kräver jordfelsbrytare (30 mA). */
const RCD_REQUIRED_KEYWORDS = [
  "badrum",
  "dusch",
  "tvättstuga",
  "uterum",
  "ute",
  "trädgård",
  "garage",
  "terrass",
  "balkong",
  "motor",
  "bastu",
];

function usageRequiresRcd(usage: string | undefined): boolean {
  if (!usage) return false;
  const lower = usage.toLowerCase();
  return RCD_REQUIRED_KEYWORDS.some((k) => lower.includes(k));
}

function isRcd(row: PanelRow): boolean {
  return Boolean(row.sensitivity) || row.component === "JFB" || row.component === "JFA";
}

/**
 * Kör valideringsregler på alla rader. Varningar returneras grupperade per rad
 * så UI:t kan rendera dem bredvid rätt cell.
 */
export function validatePanelRows(
  rows: ReadonlyArray<PanelRow>,
  sections: ReadonlyArray<Section> = [],
): PanelWarning[] {
  const out: PanelWarning[] = [];
  const seenDesignations = new Map<string, string>(); // designation → rowId
  const sectionIds = new Set(sections.map((s) => s.id as string));

  for (const row of rows) {
    // 1. Kabelarea vs säkringsström
    if (row.ampRating !== undefined && row.cableArea !== undefined) {
      const cap = maxAmpForArea(row.cableArea);
      if (cap !== undefined && row.ampRating > cap) {
        out.push({
          rowId: row.id,
          column: "cable",
          severity: "warning",
          code: "CABLE_AREA_TOO_SMALL",
          message: `Kabelarea ${row.cableArea} mm² är för liten för ${row.ampRating} A-säkring (max ${cap} A).`,
          reference: "SS 436 40 00",
        });
      }
    }

    // 2. Designation-unikhet inom panelen
    const key = row.designation.trim();
    if (key.length > 0) {
      const prev = seenDesignations.get(key);
      if (prev && prev !== row.id) {
        out.push({
          rowId: row.id,
          column: "designation",
          severity: "warning",
          code: "DESIGNATION_DUPLICATE",
          message: `Beteckningen '${key}' används redan av en annan rad.`,
        });
      } else {
        seenDesignations.set(key, row.id);
      }
    }

    // 3. JFB-krav för våtutrymmen / utomhus
    if (usageRequiresRcd(row.usage) && !isRcd(row)) {
      out.push({
        rowId: row.id,
        column: "usage",
        severity: "warning",
        code: "RCD_REQUIRED",
        message: `Användning '${row.usage}' kräver jordfelsbrytare (30 mA).`,
        reference: "IEC 60364-4-41",
      });
    }

    // 4. Dangling sectionId (sektionen finns inte längre)
    if (row.sectionId && !sectionIds.has(row.sectionId as string)) {
      out.push({
        rowId: row.id,
        column: "sectionId",
        severity: "info",
        code: "SECTION_MISSING",
        message: "Sektionen finns inte längre — flytta raden till en existerande sektion.",
      });
    }
  }

  return out;
}
